import { Link } from "wouter";
import { ArrowLeft, FileText } from "lucide-react";
import { PageMeta } from "@/components/PageMeta";

const LAST_UPDATED = "May 14, 2026";
const EFFECTIVE_DATE = "May 14, 2026";

interface SectionProps {
  number: string;
  title: string;
  children: React.ReactNode;
}

function Section({ number, title, children }: SectionProps) {
  return (
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
        <span className="text-[#7C6EEF] font-mono text-sm font-bold bg-[#7C6EEF]/10 rounded-md px-2 py-0.5">
          {number}
        </span>
        {title}
      </h2>
      <div className="text-slate-300 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export function Terms() {
  return (
    <div className="min-h-screen bg-[#030913] text-white">
      <PageMeta
        title="Terms of Service — LaunchTactix"
        description="LaunchTactix Terms of Service: subscription plans, acceptable use policy, intellectual property, and compliance responsibilities."
        canonical="https://launchtactix.com/terms"
      />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm mb-10 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to LaunchTactix
        </Link>

        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-[#7C6EEF]/15 flex items-center justify-center">
            <FileText className="w-6 h-6 text-[#7C6EEF]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
            <p className="text-slate-400 text-sm mt-0.5">
              Last updated: {LAST_UPDATED} · Effective: {EFFECTIVE_DATE}
            </p>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-[#7C6EEF]/40 via-[#14B8A6]/20 to-transparent mb-10" />

        <p className="text-slate-300 leading-relaxed mb-10">
          These Terms of Service ("Terms") govern your access to and use of the LaunchTactix
          platform — including the web application, mobile application, API, and all associated
          services (collectively, the "Platform") — operated by LaunchTactix ("we", "us", "our").
          By creating an account or using the Platform, you agree to be bound by these Terms.
          If you do not agree, do not use the Platform.
        </p>

        <Section number="1" title="Eligibility & Account Registration">
          <p>
            You must be at least 18 years old and have the legal authority to enter into a
            binding agreement on behalf of yourself or your organisation. By registering, you
            confirm that all information you provide is accurate and that you will keep it
            up to date.
          </p>
          <p>
            You are responsible for maintaining the security of your account credentials. You
            must not share your password, allow others to access your account, or use another
            person's account without authorisation. You are liable for all activity that occurs
            under your account.
          </p>
          <p>
            We reserve the right to refuse registration or terminate accounts at our sole
            discretion, including where we reasonably suspect fraudulent, abusive, or
            policy-violating activity.
          </p>
        </Section>

        <Section number="2" title="Plans, Subscriptions & Billing">
          <p>
            LaunchTactix offers the following subscription tiers:
          </p>
          <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/[0.03] mt-3">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider w-32">Plan</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider w-32">Price</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Includes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  ["Free", "$0 / month", "20 leads/month · 1 email sequence · 1 seat · basic analytics"],
                  ["Starter", "$49 / month", "Lead Discovery AI, Email Sequences, Basic CRM, Analytics, Mobile App"],
                  ["Growth", "$99 / month", "Everything in Starter · 10k leads/mo · 10 seats · SEO & GEO Engine · Social AI"],
                  ["Agency", "$299 / month", "All 40+ modules · 50 seats · White-Label · Client Portals · 100k leads/mo · REST API"],
                  ["Enterprise", "$2,499 / month", "500k leads/mo · 250 seats · Dedicated infrastructure · Custom contracts · SLA support"],
                ].map(([plan, price, includes]) => (
                  <tr key={plan} className="border-b border-white/5">
                    <td className="py-2.5 px-4 text-white font-semibold text-sm">{plan}</td>
                    <td className="py-2.5 px-4 text-[#7C6EEF] font-medium text-sm">{price}</td>
                    <td className="py-2.5 px-4 text-slate-300 text-sm">{includes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            Subscriptions are billed monthly in advance. Fees are non-refundable except where
            required by applicable law. We reserve the right to change our pricing with at
            least 30 days' notice. Continued use after a price change takes effect constitutes
            acceptance of the new pricing.
          </p>
          <p>
            Web billing is processed by{" "}
            <span className="text-white font-medium">Stripe</span>. Mobile in-app purchases are
            processed by the{" "}
            <span className="text-white font-medium">Apple App Store</span> (iOS) and{" "}
            <span className="text-white font-medium">Google Play</span> (Android) and are subject
            to the respective platform's billing terms. We never store your payment card details.
          </p>
          <p>
            To cancel your web subscription, go to{" "}
            <span className="text-white font-medium">Settings → Billing</span> before your next
            renewal date. Cancellation takes effect at the end of the current billing period —
            you retain full access until then. To cancel a mobile subscription, manage it through
            your device's app store account settings.
          </p>
          <p>
            For Enterprise plans, custom payment terms are governed by your signed order form
            or MSA. In the event of a conflict, that agreement takes precedence over these Terms.
          </p>
        </Section>

        <Section number="3" title="Acceptable Use">
          <p>
            You may use the Platform only for lawful B2B sales and marketing purposes. You agree
            not to use the Platform to:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Send spam, unsolicited commercial messages, or communications that violate any applicable anti-spam law (including CAN-SPAM, CASL, or PECR)</li>
            <li>Contact individuals who have opted out, unsubscribed, or are on suppression lists</li>
            <li>Harvest or collect personal data without a lawful basis under applicable privacy laws</li>
            <li>Violate any applicable data protection or privacy law, including GDPR, CCPA, or the Australian Privacy Act</li>
            <li>Conduct consumer-directed (B2C) outreach where prohibited without appropriate consent</li>
            <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
            <li>Transmit malware, viruses, or any code designed to disrupt, damage, or gain unauthorised access to any system</li>
            <li>Attempt to reverse engineer, disassemble, or extract source code from the Platform</li>
            <li>Resell or sublicense access to the Platform without our prior written consent (except as permitted under Agency or Enterprise plans)</li>
            <li>Interfere with or place an unreasonable load on the Platform's infrastructure</li>
            <li>Use the Platform in any way that violates applicable law or regulation</li>
          </ul>
          <p>
            Breach of this section may result in immediate suspension or termination of your
            account without refund.
          </p>
        </Section>

        <Section number="4" title="B2B Outreach & Compliance Responsibility">
          <p>
            The Platform enables you to discover business contact information and send email and
            SMS outreach on your behalf. You are solely responsible for ensuring that your use
            of these features complies with all applicable laws, including:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>CAN-SPAM Act (United States)</li>
            <li>CASL — Canada's Anti-Spam Legislation</li>
            <li>PECR — Privacy and Electronic Communications Regulations (United Kingdom)</li>
            <li>GDPR — General Data Protection Regulation (European Union / EEA)</li>
            <li>Australian Spam Act 2003</li>
            <li>Any other anti-spam, telemarketing, or data protection law in your jurisdiction</li>
          </ul>
          <p>
            You are responsible for: (a) having a lawful basis to contact each prospect; (b) honouring
            all opt-out and unsubscribe requests promptly; (c) maintaining accurate suppression lists;
            and (d) ensuring your email content includes required disclosures (sender identity, physical
            address, unsubscribe mechanism). LaunchTactix provides tools to assist with compliance
            but does not act as your legal adviser, and compliance remains your responsibility.
          </p>
          <p className="p-3 rounded-lg bg-[#14B8A6]/10 border border-[#14B8A6]/20 text-[#14B8A6] text-sm font-medium">
            LaunchTactix is a B2B platform. It is not designed or licensed for consumer (B2C)
            marketing. You must not use it to contact individuals in a purely personal capacity.
          </p>
        </Section>

        <Section number="5" title="AI-Generated Content">
          <p>
            The Platform uses AI models to generate email copy, social media posts, proposals,
            images, lead scores, and other content. You acknowledge that:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>AI-generated content may be inaccurate, incomplete, or inappropriate — you are responsible for reviewing and approving all content before sending or publishing it</li>
            <li>We do not guarantee that AI outputs are free from errors, bias, or legal risk</li>
            <li>You own the content you generate using the Platform, subject to any restrictions imposed by the underlying AI model provider</li>
            <li>We do not use your content or data to train AI models</li>
          </ul>
        </Section>

        <Section number="6" title="White-Label & Agency Use">
          <p>
            Agency and Enterprise plan subscribers may white-label the Platform under their own
            brand and provision it to their own clients ("End Clients"). As a white-label operator,
            you agree to:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Ensure each End Client's use complies with these Terms and all applicable law</li>
            <li>Take full responsibility for all activity conducted by your End Clients on the Platform</li>
            <li>Maintain your own terms of service with each End Client governing their use of your white-labeled product</li>
            <li>Not represent LaunchTactix as your own proprietary software in contexts where doing so would be misleading to regulators or courts</li>
          </ul>
          <p>
            You may not resell or sublicense access to the Platform outside of your approved
            white-label arrangement without our written consent.
          </p>
        </Section>

        <Section number="7" title="Intellectual Property">
          <p>
            The Platform, including its software, design, trademarks, and documentation, is owned
            by LaunchTactix and protected by intellectual property laws. These Terms do not grant
            you any rights to our intellectual property except the limited right to use the Platform
            as permitted herein.
          </p>
          <p>
            You retain ownership of all data, content, and materials you upload to or generate
            using the Platform ("Your Content"). By using the Platform, you grant us a limited,
            non-exclusive licence to process, store, and transmit Your Content solely as necessary
            to provide the Platform's features. We do not claim ownership of Your Content.
          </p>
          <p>
            If you provide us with feedback or suggestions about the Platform, we may use that
            feedback without restriction or compensation to you.
          </p>
        </Section>

        <Section number="8" title="Data & Privacy">
          <p>
            Our collection and use of personal data is governed by our{" "}
            <Link href="/privacy" className="text-[#7C6EEF] hover:underline">
              Privacy Policy
            </Link>
            , which is incorporated into these Terms by reference. By using the Platform, you
            consent to our data practices as described in the Privacy Policy.
          </p>
          <p>
            Where you process personal data of third parties (such as prospect contact details)
            using the Platform, you are the data controller and we act as a data processor on
            your behalf. You are responsible for ensuring you have a lawful basis for that processing.
          </p>
        </Section>

        <Section number="9" title="Uptime & Service Levels">
          <p>
            We aim to maintain high platform availability but do not guarantee uninterrupted
            service. Scheduled maintenance, infrastructure incidents, or events outside our
            reasonable control may cause temporary downtime.
          </p>
          <p>
            Enterprise customers with a signed Service Level Agreement (SLA) are governed by
            the uptime commitments and remedies set out in that agreement. For all other plans,
            we provide the Platform on a commercially reasonable efforts basis without any
            uptime warranty.
          </p>
        </Section>

        <Section number="10" title="Suspension & Termination">
          <p>
            We may suspend or terminate your access to the Platform, with or without notice, if:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>You breach any provision of these Terms</li>
            <li>Your account is used for spam, abuse, or illegal activity</li>
            <li>You fail to pay any amounts due</li>
            <li>We are required to do so by law or court order</li>
          </ul>
          <p>
            You may terminate your account at any time via{" "}
            <span className="text-white font-medium">Settings → Delete Account</span>. Upon
            termination, your data will be deleted within 30 days, except where retention is
            required by law.
          </p>
          <p>
            Sections 4, 7, 11, 12, and 13 survive termination of these Terms.
          </p>
        </Section>

        <Section number="11" title="Disclaimer of Warranties">
          <p>
            The Platform is provided "as is" and "as available" without warranty of any kind.
            To the fullest extent permitted by law, we disclaim all warranties, express or
            implied, including but not limited to implied warranties of merchantability, fitness
            for a particular purpose, and non-infringement.
          </p>
          <p>
            We do not warrant that: (a) the Platform will meet your specific requirements;
            (b) the Platform will be uninterrupted, error-free, or secure; (c) any results
            obtained from the Platform (including AI-generated content or lead data) will be
            accurate, complete, or reliable; or (d) defects in the Platform will be corrected.
          </p>
        </Section>

        <Section number="12" title="Limitation of Liability">
          <p>
            To the fullest extent permitted by applicable law, LaunchTactix and its officers,
            directors, employees, and agents shall not be liable for any indirect, incidental,
            special, consequential, or punitive damages — including loss of profits, data, or
            business opportunities — arising out of or relating to your use of the Platform,
            even if we have been advised of the possibility of such damages.
          </p>
          <p>
            Our total aggregate liability to you for any claim arising out of or relating to
            these Terms or the Platform shall not exceed the greater of: (a) the total fees
            paid by you to us in the 12 months immediately preceding the claim; or (b) USD $100.
          </p>
          <p>
            Some jurisdictions do not allow exclusion or limitation of certain damages, so the
            above limitations may not apply to you to the extent prohibited by local law.
          </p>
        </Section>

        <Section number="13" title="Indemnification">
          <p>
            You agree to indemnify, defend, and hold harmless LaunchTactix and its officers,
            directors, employees, and agents from any claims, damages, losses, liabilities,
            costs, and expenses (including reasonable legal fees) arising out of or relating to:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Your use of the Platform in violation of these Terms or applicable law</li>
            <li>Your Content, including any claim that it infringes a third party's rights</li>
            <li>Your outreach campaigns, including any violation of anti-spam or privacy law</li>
            <li>Your white-label operations or the conduct of your End Clients</li>
          </ul>
        </Section>

        <Section number="14" title="Governing Law & Disputes">
          <p>
            These Terms are governed by the laws of the jurisdiction in which LaunchTactix is
            registered, without regard to conflict-of-law principles. Any dispute arising under
            these Terms shall first be subject to good-faith negotiation. If unresolved within
            30 days, disputes shall be submitted to binding arbitration, except that either party
            may seek injunctive or other equitable relief in a court of competent jurisdiction
            to prevent irreparable harm.
          </p>
          <p>
            Nothing in this section limits your rights as a consumer under applicable mandatory
            local law.
          </p>
        </Section>

        <Section number="15" title="Changes to These Terms">
          <p>
            We may update these Terms from time to time. We will notify you of material changes
            via email or an in-app notice at least 14 days before they take effect. The "Last
            updated" date at the top of this page always reflects the most current version.
            Continued use of the Platform after the effective date of revised Terms constitutes
            your acceptance of those Terms.
          </p>
          <p>
            If you do not agree to a material change, you may terminate your account before
            the change takes effect and receive a pro-rata refund of any prepaid fees for the
            unused portion of your current billing period.
          </p>
        </Section>

        <Section number="16" title="Miscellaneous">
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><span className="text-white font-medium">Entire agreement.</span> These Terms, together with the Privacy Policy and any signed order form or MSA, constitute the entire agreement between you and LaunchTactix regarding the Platform.</li>
            <li><span className="text-white font-medium">Severability.</span> If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.</li>
            <li><span className="text-white font-medium">No waiver.</span> Failure to enforce any provision of these Terms shall not constitute a waiver of our right to enforce it in the future.</li>
            <li><span className="text-white font-medium">Assignment.</span> You may not assign or transfer your rights or obligations under these Terms without our prior written consent. We may assign these Terms in connection with a merger, acquisition, or sale of all or substantially all of our assets.</li>
            <li><span className="text-white font-medium">Force majeure.</span> We are not liable for any delay or failure to perform resulting from causes outside our reasonable control.</li>
          </ul>
        </Section>

        <Section number="17" title="Contact">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-1 text-sm">
            <p className="text-white font-semibold text-base mb-2">LaunchTactix</p>
            <p>
              Legal enquiries:{" "}
              <a href="mailto:legal@launchtactix.com" className="text-[#7C6EEF] hover:underline">
                legal@launchtactix.com
              </a>
            </p>
            <p>
              General support:{" "}
              <a href="mailto:support@launchtactix.com" className="text-[#7C6EEF] hover:underline">
                support@launchtactix.com
              </a>
            </p>
            <p className="mt-2">
              <a href="https://launchtactix.com/terms" className="text-[#7C6EEF] hover:underline">
                https://launchtactix.com/terms
              </a>
            </p>
          </div>
        </Section>

        <div className="h-px bg-gradient-to-r from-[#7C6EEF]/40 via-[#14B8A6]/20 to-transparent mt-12 mb-8" />

        <p className="text-center text-slate-500 text-sm">
          © 2026 LaunchTactix · All rights reserved ·{" "}
          <Link href="/privacy" className="hover:text-slate-300 transition-colors">
            Privacy Policy
          </Link>
          {" · "}
          <a href="mailto:legal@launchtactix.com" className="hover:text-slate-300 transition-colors">
            legal@launchtactix.com
          </a>
        </p>
      </div>
    </div>
  );
}
